import { useState } from "react";

import { fromUnixTime } from "date-fns";

import { useAsyncCall, useDelay, useMutation as useSpecialQuery } from "hooks";
import createResolverFactory from "components/QueryClient/ResolverFactory";

const queryFactory = () => {
  const query = `query getChangePlanEstimate($newPlanKey: String, $coupon: String) {
        changePlanEstimate(newPlanKey: $newPlanKey, coupon: $coupon) {
            currency
            newCbPlanId
            billingPeriod
            updatedPlanFeatures
            currentPlan {
                name
                usedSeats
                numSeats
                planMultiple
                unitPrice
                nextBillingPrice
                nextBillingDate
                deltaPrice
            }
            updatedPlan {
                name
                usedSeats
                numSeats
                planMultiple
                unitPrice            
                nextBillingPrice
                nextBillingDate
                deltaPrice
            }
            hasExistingCoupon
            couponEstimate {
                coupons {
                   id
                   discountAmount
                   discountDetails
                }
            }
            couponError
            allowApplyAtRenewal
        }
    }`;

  const resolver = ({ data }) => {
    const parseToDate = (dateString) => {
      return dateString ? fromUnixTime(dateString) : null;
    };

    return {
      ...data.changePlanEstimate,
      coupon: data.changePlanEstimate.couponEstimate?.coupons?.length
        ? data.changePlanEstimate.couponEstimate.coupons[0]
        : null,
      currentPlan: {
        ...data.changePlanEstimate.currentPlan,
        nextBillingDate: parseToDate(
          data.changePlanEstimate.currentPlan.nextBillingDate
        ),
      },
      updatedPlan: {
        ...data.changePlanEstimate.updatedPlan,
        nextBillingDate: parseToDate(
          data.changePlanEstimate.updatedPlan.nextBillingDate
        ),
      },
    };
  };

  return { query: query, resolver: createResolverFactory(resolver) };
};

function useGetChangePlanEstimate(initialCoupon) {
  const [callEstimate, loadingEstimate] = useAsyncCall();
  const [callEstimateWithCoupon, validatingCoupon] = useAsyncCall();
  const [error, setError] = useState({});
  const [data, setData] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(initialCoupon || null);
  const delayCallback = useDelay(500);
  const query = useSpecialQuery(queryFactory);

  const estimate = ({ newPlanKey, coupon = undefined }) => {
    const setErr = (e) => {
      setError((prev) => {
        return {
          ...prev,
          [coupon ? "estimateWithCoupon" : "estimate"]: e,
        };
      });
    };

    return async () => {
      try {
        setErr(null);

        const planEstimate = await query({ newPlanKey, coupon });
        if (planEstimate) setData(planEstimate);

        return planEstimate;
      } catch (e) {
        console.error("Failed to get change plan estimate", e);
        setErr(
          "Unable to estimate the pricing for the new plan. Please retry or contact support for assistance."
        );
        setData({});

        return null;
      }
    };
  };

  const {
    coupon = {},
    hasExistingCoupon = false,
    couponError,
    ...restData
  } = data || {};

  return {
    ...restData,
    loading: loadingEstimate || !data,
    error: error?.estimate,
    estimate: async (newPlanKey) => {
      return delayCallback(async () => {
        return callEstimate(
          estimate({ newPlanKey: newPlanKey, coupon: activeCoupon })
        );
      });
    },
    estimateWithCoupon: async (k, c) => {
      setActiveCoupon(c);
      return callEstimateWithCoupon(estimate({ newPlanKey: k, coupon: c }));
    },
    coupon: {
      ...coupon,
      hasExistingCoupon: hasExistingCoupon,
      validating: validatingCoupon,
      error: couponError,
    },
  };
}

export default useGetChangePlanEstimate;
